import React, { createContext, useContext } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MyLayout } from './components/MyLayout';
import PrivateRoutes from './components/PrivateRoutes';
import { UserLogin } from './pages/UserLogin';
import ForgotPassword from './pages/ForgotPassword';
import NoFoundPage from './pages/404';
import Authorizer from './api/Authorizer';
import { GlobalStateContext } from './context/GlobalContext';
import { PageLoading } from '@ant-design/pro-layout';
import FileApproval from './components/FileApproval';
import GenerateReports from './components/GenerateReports';
import Statistics from './components/Statistics';
import PerformanceReports from './components/PerformanceReports';
import FinancialReports from './components/FinancialReports';
import InternalDashboard from './components/InternalDashboard';
import Dashboard from './components/Dashboard';
import { UserAuth } from './context/AuthContext';
import UnauthorizedPage from './pages/403';
import { Access } from './components/Access';
import CustomerInvoices from './components/CustomerInvoices';
import YouTubeMetadata from './pages/YouTubeMetadata';
import AssetIncomeReport from './pages/AssetIncomeReport';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

export const AuthorizerContext = createContext<Authorizer | undefined>(undefined);

// Create a query client with default options
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // Don't refetch on window focus
            retry: 1, // Retry failed requests once
            staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
        },
    },
});

function App() {
    const location = useLocation();
    const background = location.state && location.state.background;
    const { authorizing } = useContext(GlobalStateContext);
    const { can, user } = UserAuth();

    const isUserReady = user !== undefined && user !== null;

    return (
        <QueryClientProvider client={queryClient}>
            <div className='App'>
                <Routes location={background || location}>
                <Route element={<PrivateRoutes />}>
                    <Route element={<MyLayout />}>
                        {/*////////////////*/}
                        {/*//// Routes ////*/}
                        {/*////////////////*/}
                        <Route
                            path='/dashboard'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::customer::finance')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <Dashboard />
                                </Access>
                            }
                        />

                        <Route
                            path='/internaldashboard'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::manager')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <InternalDashboard />
                                </Access>
                            }
                        />

                        <Route
                            path='/fileapproval'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::manager')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <FileApproval />
                                </Access>
                            }
                        />

                        <Route
                            path='/management/generatereports'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'management::reporting::data')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <GenerateReports />
                                </Access>
                            }
                        />

                        <Route
                            path='/management/statistics'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'management::reporting::data')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <Statistics />
                                </Access>
                            }
                        />

                        <Route
                            path='/management/youtube-metadata'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::manager')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <YouTubeMetadata />
                                </Access>
                            }
                        />

                        <Route
                            path='/management/asset-income'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'management::reporting::data')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <AssetIncomeReport />
                                </Access>
                            }
                        />

                        <Route
                            path='/reports/performance'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::customer::performance')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <PerformanceReports />
                                </Access>
                            }
                        />

                        <Route
                            path='/reports/financial'
                            element={
                                <Access
                                    accessible={!authorizing && isUserReady && can('read', 'portal::data::customer::finance')}
                                    fallback={authorizing || !isUserReady ? <PageLoading /> : <UnauthorizedPage />}
                                >
                                    <FinancialReports />
                                </Access>
                            }
                        />

                        {/*//////////////////*/}
                        {/*//// Navigate ////*/}
                        {/*//////////////////*/}
                        <Route
                            path='/'
                            element={
                                authorizing || !isUserReady ? (
                                    <PageLoading />
                                ) : can('read', 'portal::data::customer::finance') ? (
                                    <Navigate to={'/dashboard'} />
                                ) : can('read', 'portal::data::customer::performance') ? (
                                   <Navigate to={'/reports/performance'} />
                               ) : can('read', 'portal::data::manager') ? (
                                   <Navigate to={'/internaldashboard'} />
                                ) : (
                                    <UnauthorizedPage />
                                )
                            }
                        />
                        <Route path='reports' element={<Navigate to={'/reports/performance'} />} />
                        <Route path='management' element={<Navigate to={'/management/statistics'} />} />
                        {/* <Route path='invoices' element={<Navigate to={'/invoices'} />} /> */}

                        {/*<Route path='/' element={*/}
                        {/*    <Access accessible={can('read', 'portal::data::customer::finance')} fallback={<></>}>*/}
                        {/*        <Dashboard/>*/}
                        {/*    </Access>*/}
                        {/*}/>*/}

                        {/*<Access accessible={can('read', 'portal::data::customer::performance')} fallback={<></>}>*/}
                        {/*    <PerformanceReports/>*/}
                        {/*</Access>*/}
                    </Route>
                </Route>
                <Route path='/login' element={<UserLogin />} />
                <Route path='/forgot-password' element={<ForgotPassword />} />
                <Route path='*' element={<NoFoundPage />} />
            </Routes>
            <ReactQueryDevtools initialIsOpen={false} />
        </div>
        </QueryClientProvider>
    );
}

export default App;
